import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import type { AuditEntryItem } from '../use-audit-log';
import { eventTypeKey, humanizeEventType } from '../event-type-label';
import { parseUserAgent } from '../../../lib/parse-user-agent';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

const SURFACE = `color-mix(in oklab, ${sentinel.surfaceElevated} 60%, transparent)`; // surfaceElevated (matches session-row)
const FONT_DISPLAY = sentinel.fontDisplay;
const FONT_MONO = sentinel.fontMono;

const styles = stylex.create({
  row: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    alignItems: 'start',
    columnGap: '0.875rem',
    paddingTop: '0.875rem',
    paddingBottom: '0.875rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    borderRadius: '0.625rem',
    backgroundColor: SURFACE,
    transitionProperty: 'border-color, background-color',
    transitionDuration: '200ms',
    transitionTimingFunction: sentinel.easeOut,
  },
  // A leading status rail: a small rounded square whose tint encodes the
  // outcome. Pairs with the textual status pill so colour is never the sole
  // signal, and gives each row a stable visual anchor on the left edge.
  rail: {
    width: '2rem',
    height: '2rem',
    borderRadius: '0.5rem',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    borderWidth: '1px',
    borderStyle: 'solid',
  },
  railSuccess: {
    color: 'oklch(82% 0.16 145.76)',
    backgroundColor: `color-mix(in oklab, ${sentinel.riskSafe} 14%, transparent)`,
    borderColor: `color-mix(in oklab, ${sentinel.riskSafe} 32%, transparent)`,
  },
  railFailure: {
    color: 'oklch(78% 0.18 16.23)',
    backgroundColor: `color-mix(in oklab, ${sentinel.textError} 14%, transparent)`,
    borderColor: `color-mix(in oklab, ${sentinel.textError} 34%, transparent)`,
  },
  body: { minWidth: 0, display: 'grid', gap: '0.3125rem' },
  main: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    alignItems: 'center',
  },
  label: {
    fontFamily: FONT_DISPLAY,
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: sentinel.textBody,
    letterSpacing: '-0.005em',
    flexGrow: 1,
    minWidth: 0,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3125rem',
    paddingTop: '0.0625rem',
    paddingBottom: '0.0625rem',
    paddingLeft: '0.4375rem',
    paddingRight: '0.5625rem',
    borderRadius: '999px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.01em',
    lineHeight: 1.4,
    borderWidth: '1px',
    borderStyle: 'solid',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  statusSuccess: {
    color: 'oklch(82% 0.16 145.76)',
    backgroundColor: `color-mix(in oklab, ${sentinel.riskSafe} 16%, transparent)`,
    borderColor: `color-mix(in oklab, ${sentinel.riskSafe} 32%, transparent)`,
  },
  statusFailure: {
    color: 'oklch(78% 0.18 16.23)',
    backgroundColor: `color-mix(in oklab, ${sentinel.textError} 16%, transparent)`,
    borderColor: `color-mix(in oklab, ${sentinel.textError} 36%, transparent)`,
  },
  meta: {
    fontSize: '0.8125rem',
    color: sentinel.textMuted,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.25rem 0.875rem',
  },
  metaItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    minWidth: 0,
  },
  metaIcon: { color: sentinel.textSubtle, flexShrink: 0 },
  time: { color: sentinel.textMuted },
  mono: {
    fontFamily: FONT_MONO,
    fontSize: '0.75rem',
    color: sentinel.textMuted,
  },
});

const STATUS_ICON_PROPS = {
  width: 12,
  height: 12,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const META_ICON_PROPS = {
  width: 13,
  height: 13,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function CheckIcon() {
  return (
    <svg {...STATUS_ICON_PROPS}>
      <path d="M3 8.5 6.5 12 13 4.5" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg {...STATUS_ICON_PROPS}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg {...META_ICON_PROPS}>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 5v3l2 1.5" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg {...META_ICON_PROPS}>
      <circle cx="8" cy="8" r="6" />
      <path d="M2 8h12M8 2c1.8 1.6 2.8 3.8 2.8 6S9.8 12.4 8 14C6.2 12.4 5.2 10.2 5.2 8S6.2 3.6 8 2z" />
    </svg>
  );
}

function DeviceIcon() {
  return (
    <svg {...META_ICON_PROPS}>
      <rect x="2" y="3" width="12" height="8" rx="1" />
      <path d="M6 14h4M8 11v3" />
    </svg>
  );
}

/** Merge a stable marker class onto StyleX-generated props without clobbering
 *  the StyleX classes (a second `className` JSX attribute would win and drop
 *  them). The marker classes are e2e/unit-test hooks, not style carriers. */
function withMarker(props: { className?: string; style?: React.CSSProperties }, marker: string) {
  return { ...props, className: props.className ? `${props.className} ${marker}` : marker };
}

export function AuditRow({ entry }: { entry: AuditEntryItem }) {
  const { t, i18n } = useTranslation('audit-log');

  const key = eventTypeKey(entry.event_type);
  const label = i18n.exists(key, { ns: 'audit-log' })
    ? t(key)
    : humanizeEventType(entry.event_type);

  const isSuccess = entry.status === 'success';
  const when = new Date(entry.created_at).toLocaleString();
  const device = entry.user_agent ? parseUserAgent(entry.user_agent, entry.user_agent) : null;

  return (
    <li data-status={entry.status} {...stylex.props(styles.row)}>
      <span
        aria-hidden="true"
        {...stylex.props(styles.rail, isSuccess ? styles.railSuccess : styles.railFailure)}
      >
        {isSuccess ? <CheckIcon /> : <CrossIcon />}
      </span>
      <div {...stylex.props(styles.body)}>
        <div {...stylex.props(styles.main)}>
          <span {...stylex.props(styles.label)}>{label}</span>
          <span
            data-status={entry.status}
            {...stylex.props(
              styles.statusBadge,
              isSuccess ? styles.statusSuccess : styles.statusFailure,
            )}
          >
            {isSuccess ? <CheckIcon /> : <CrossIcon />}
            {t(`status.${entry.status}`)}
          </span>
        </div>
        <div {...stylex.props(styles.meta)}>
          <span {...stylex.props(styles.metaItem)}>
            <span {...stylex.props(styles.metaIcon)}>
              <ClockIcon />
            </span>
            <time dateTime={entry.created_at} {...stylex.props(styles.time)}>
              {when}
            </time>
          </span>
          {entry.ip_address ? (
            <span {...withMarker(stylex.props(styles.metaItem), 'audit-row__ip')}>
              <span {...stylex.props(styles.metaIcon)}>
                <GlobeIcon />
              </span>
              <span {...stylex.props(styles.mono)}>{entry.ip_address}</span>
            </span>
          ) : null}
          {device ? (
            <span {...withMarker(stylex.props(styles.metaItem), 'audit-row__ua')}>
              <span {...stylex.props(styles.metaIcon)}>
                <DeviceIcon />
              </span>
              <span>{device}</span>
            </span>
          ) : null}
        </div>
      </div>
    </li>
  );
}
